import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { supabase } from '../../supabaseClient';
import { useAuth } from '../../AuthContext'; // <-- CORREGIDO: Usar AuthContext
import { Loader2, ShoppingCart, Coffee, Utensils, Sandwich, IceCream, ChevronDown, ChevronUp, AlertTriangle } from 'lucide-react';
import DatePicker from 'react-datepicker'; // <-- Funcionará después de npm install
import "react-datepicker/dist/react-datepicker.css"; // <-- Funcionará después de npm install

// --- Componente de Tarjeta de Transacción (Sin cambios) ---
const TransactionCard = ({ transaction }) => {
    const [isOpen, setIsOpen] = useState(false);
    const getCategoryIcon = (category) => {
        const cat = category?.toLowerCase() || '';
        if (cat.includes('bebidas')) return <Coffee size={20} />;
        if (cat.includes('almuerzos')) return <Utensils size={20} />;
        if (cat.includes('snacks')) return <Sandwich size={20} />;
        if (cat.includes('postres')) return <IceCream size={20} />;
        return <ShoppingCart size={20} />;
    };

    return (
        <div className="bg-white rounded-xl shadow-sm transition hover:shadow-md">
            <div className="p-4 cursor-pointer flex justify-between items-center" onClick={() => setIsOpen(!isOpen)}>
                <div className="flex items-center gap-4 overflow-hidden mr-2">
                    <div className="p-3 bg-gray-100 rounded-full text-gray-600 flex-shrink-0">
                        {getCategoryIcon(transaction.items[0]?.product_category)}
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-800 truncate">
                            Compra del {new Date(transaction.created_at).toLocaleDateString('es-SV', { weekday: 'long', day: 'numeric', month: 'long' })}
                        </p>
                        <p className="text-sm text-gray-500">
                            {transaction.items.length} {transaction.items.length > 1 ? 'productos' : 'producto'}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-4 flex-shrink-0">
                     <span className="font-bold text-lg text-red-600">
                        -${parseFloat(transaction.total_amount).toFixed(2)}
                    </span>
                    {isOpen ? <ChevronUp size={20} className="text-gray-500" /> : <ChevronDown size={20} className="text-gray-500" />}
                </div>
            </div>
            {isOpen && (
                <div className="border-t border-gray-100 px-6 py-4">
                    <ul className="space-y-2">
                        {transaction.items.map((item, index) => (
                            <li key={item.product_id || index} className="flex justify-between text-sm">
                                <span className="text-gray-700">{item.quantity} x {item.product_name || 'Producto desconocido'}</span>
                                <span className="text-gray-500">${(item.price_at_sale * item.quantity).toFixed(2)}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

// --- Sub-componente para historial de Estudiante/Padre ---
const StudentParentPurchaseHistory = () => {
    const { userData, userType } = useAuth();
    // Estados para el selector (igual que en Resumen.js)
    const [students, setStudents] = useState([]);
    const [selectedStudentId, setSelectedStudentId] = useState('');
    
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [startDate, setStartDate] = useState(new Date(new Date().setDate(new Date().getDate() - 30)));
    const [endDate, setEndDate] = useState(new Date());

    // Configura el selector de estudiantes
    useEffect(() => {
        if (userType === 'parent' && userData?.students) {
            setStudents(userData.students);
            if (userData.students.length > 0) setSelectedStudentId(userData.students[0].id);
        } else if (userType === 'student' && userData) {
            setStudents([userData]);
            setSelectedStudentId(userData.id);
        }
    }, [userData, userType]);

    // Carga los datos cuando cambia el estudiante o las fechas
    const fetchData = useCallback(async () => {
        if (!selectedStudentId) {
            setTransactions([]);
            return;
        }
        setLoading(true);
        try {
            const { data, error } = await supabase
                .from('detailed_transactions') // Usa la vista de estudiantes
                .select('*')
                .eq('student_id', selectedStudentId)
                .gte('transaction_created_at', startDate.toISOString())
                .lte('transaction_created_at', new Date(endDate.setHours(23, 59, 59)).toISOString())
                .order('transaction_created_at', { ascending: false });

            if (error) throw error;

            const grouped = (data || []).reduce((acc, item) => {
                if (!acc[item.transaction_id]) {
                    acc[item.transaction_id] = { id: item.transaction_id, created_at: item.transaction_created_at, total_amount: item.total_amount, items: [] };
                }
                acc[item.transaction_id].items.push(item);
                return acc;
            }, {});
            setTransactions(Object.values(grouped).sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
        } catch (error) {
            console.error("Error fetching student transactions:", error);
            alert("Error al cargar historial: " + error.message);
        } finally {
            setLoading(false);
        }
    }, [selectedStudentId, startDate, endDate]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const totalSpent = useMemo(() => {
        return transactions.reduce((sum, tx) => sum + tx.total_amount, 0);
    }, [transactions]);

    return (
        <>
            {/* Selector de estudiante (solo para padres con >1 hijo) */}
            {userType === 'parent' && students.length > 1 && (
                <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Selecciona un Estudiante</label>
                    <select
                        value={selectedStudentId || ''}
                        onChange={(e) => setSelectedStudentId(e.target.value)}
                        className="w-full max-w-xs p-2 border rounded-md bg-white shadow-sm"
                    >
                        {students.map(s => (
                            <option key={s.id} value={s.id}>{s.first_name} {s.first_last_name}</option>
                        ))}
                    </select>
                </div>
            )}
            {/* Selector de Fechas */}
            <div className="flex flex-wrap items-center gap-4 mb-6 p-4 bg-white rounded-lg shadow-sm">
                <label className="text-sm font-medium text-gray-700">Desde:</label>
                <DatePicker selected={startDate} onChange={(date) => setStartDate(date)} className="p-2 border rounded-md" dateFormat="dd/MM/yyyy" />
                <label className="text-sm font-medium text-gray-700">Hasta:</label>
                <DatePicker selected={endDate} onChange={(date) => setEndDate(date)} className="p-2 border rounded-md" dateFormat="dd/MM/yyyy" />
            </div>
            
            {loading ? (
                <div className="flex justify-center items-center h-64"><Loader2 className="animate-spin text-orange-500" size={32} /></div>
            ) : transactions.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No hay compras en el rango de fechas seleccionado.</p>
            ) : (
                <div className="space-y-4">
                    <div className="p-4 bg-red-50 text-red-700 rounded-lg font-semibold">
                        Total Gastado en este período: ${totalSpent.toFixed(2)}
                    </div>
                    {transactions.map(tx => <TransactionCard key={tx.id} transaction={tx} />)}
                </div>
            )}
        </>
    );
};

// --- Sub-componente para historial de Maestro ---
const TeacherPurchaseHistory = () => {
    const { userData } = useAuth(); // Obtener los datos del maestro logueado
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [startDate, setStartDate] = useState(new Date(new Date().setDate(new Date().getDate() - 30)));
    const [endDate, setEndDate] = useState(new Date());

    const fetchData = useCallback(async () => {
        if (!userData?.id) {
            setTransactions([]);
            return;
        }
        setLoading(true);
        try {
            // Usa la VISTA 'detailed_transactions_teacher' que creamos
            const { data, error } = await supabase
                .from('detailed_transactions_teacher')
                .select('*')
                .eq('teacher_id', userData.id)
                .gte('transaction_created_at', startDate.toISOString())
                .lte('transaction_created_at', new Date(endDate.setHours(23, 59, 59)).toISOString())
                .order('transaction_created_at', { ascending: false });

            if (error) throw error;

            const grouped = (data || []).reduce((acc, item) => {
                if (!acc[item.transaction_id]) {
                    acc[item.transaction_id] = { id: item.transaction_id, created_at: item.transaction_created_at, total_amount: item.total_amount, items: [] };
                }
                acc[item.transaction_id].items.push(item);
                return acc;
            }, {});
            setTransactions(Object.values(grouped).sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
        } catch (error) {
            console.error("Error fetching teacher transactions:", error);
            alert("Error al cargar historial: " + error.message);
        } finally {
            setLoading(false);
        }
    }, [userData, startDate, endDate]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const totalSpent = useMemo(() => {
        return transactions.reduce((sum, tx) => sum + tx.total_amount, 0);
    }, [transactions]);

    return (
        <>
            <div className="flex flex-wrap items-center gap-4 mb-6 p-4 bg-white rounded-lg shadow-sm">
                <label className="text-sm font-medium text-gray-700">Desde:</label>
                <DatePicker selected={startDate} onChange={(date) => setStartDate(date)} className="p-2 border rounded-md" dateFormat="dd/MM/yyyy" />
                <label className="text-sm font-medium text-gray-700">Hasta:</label>
                <DatePicker selected={endDate} onChange={(date) => setEndDate(date)} className="p-2 border rounded-md" dateFormat="dd/MM/yyyy" />
            </div>
            
            {loading ? (
                <div className="flex justify-center items-center h-64"><Loader2 className="animate-spin text-orange-500" size={32} /></div>
            ) : transactions.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No hay compras en el rango de fechas seleccionado.</p>
            ) : (
                <div className="space-y-4">
                    <div className="p-4 bg-red-50 text-red-700 rounded-lg font-semibold">
                        Total Gastado en este período: ${totalSpent.toFixed(2)}
                    </div>
                    {transactions.map(tx => <TransactionCard key={tx.id} transaction={tx} />)}
                </div>
            )}
        </>
    );
};


// --- Componente Principal (Switcher) ---
const HistorialCompras = () => {
    const { userType, loading } = useAuth();

    if (loading) {
         return <div className="flex justify-center items-center h-64"><Loader2 className="animate-spin text-orange-500" size={32} /></div>;
    }
    
    return (
        <div className="space-y-6">
            <h1 className="text-3xl font-bold text-gray-800">Historial de Compras</h1>
            
            {userType === 'teacher' && <TeacherPurchaseHistory />}
            
            {(userType === 'student' || userType === 'parent') && <StudentParentPurchaseHistory />}

            {userType !== 'teacher' && userType !== 'student' && userType !== 'parent' && (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-center">
                    <AlertTriangle className="mx-auto h-12 w-12 text-yellow-400" />
                    <h3 className="mt-2 text-lg font-medium text-yellow-800">Vista no disponible</h3>
                    <p className="mt-1 text-sm text-yellow-700">
                      El historial de compras solo está disponible para estudiantes, padres o maestros.
                    </p>
                </div>
            )}
        </div>
    );
};

export default HistorialCompras;